import {
  useContext, useEffect, useReducer, useMemo,
} from 'react';
import {
  useLazyQuery, useMutation, useQuery, useApolloClient,
} from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Link as MuiLink, Skeleton } from '../../1-primative';
import {
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
  SelectField,
  Pagination,
  CardContent,
  TextField,
  DateField,
  Card,
  Badge,
  Checkbox,
  Button,
} from '../../2-component';
import { usePermissions, UserContext } from '../../../providers/userContextProvider';
import { usePageState } from '../../../util/usePageState';
import { AccountTypeSelect, FilterModal } from '../../3-pattern';
import { controlColumnStyles, controlWrapperStyles } from '../reconciliation/components/activities';
import ConfirmationDialog, { ConfirmButtonColors } from '../../3-pattern/confirmationDialog/confirmationDialog';
import StatementsSelectionInfo from './components/statementsSelectionInfo';
import { FETCH_ACCOUNT_STATEMENT, FETCH_STATEMENTS_FOR_REVIEW, TRANSITION_ACCOUNT_STATEMENTS } from './statementsReview.queries';
import { generateClientNameString } from '../../../util';

export enum StatementStates {
  AWAITING_REVIEW = 'AWAITING_REVIEW',
  FAILED = 'FAILED',
  RECONCILED = 'RECONCILED',
}

enum AccountStatementTransitions {
  RECONCILE = 'reconcile',
  FAIL = 'fail',
}

interface Statement {
  id: string;
  state: string;
  type: string;
  account: {
    id: string;
    type: string;
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    organization: {
      name: string;
    };
  };
}

interface StatementsState {
  pagination: {
    page: number;
    pageSize: number;
  };

  selection: {
    selected: string[];
    transitionAll: boolean;
  };

  ui: {
    transitioning: boolean;
    openConfirmationDialog: boolean;
    currentStatement: Statement | null;
  };

  filters: {
    typeGroup: string;
    afterDate: string;
    beforeDate: string;
    accountType: string;
    period: string;
  };
}

type StatementsAction =
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_SELECTED'; payload: string[] }
  | { type: 'TOGGLE_SELECTION'; payload: string }
  | { type: 'SELECT_ALL_ON_PAGE'; payload: string[] }
  | { type: 'DESELECT_ALL_ON_PAGE'; payload: string[] }
  | { type: 'SET_TRANSITION_ALL'; payload: boolean }
  | { type: 'SET_TRANSITIONING'; payload: boolean }
  | { type: 'SET_CONFIRMATION_DIALOG'; payload: boolean }
  | { type: 'SET_FILTER_TYPE_GROUP'; payload: string }
  | { type: 'SET_FILTER_AFTER_DATE'; payload: string }
  | { type: 'SET_FILTER_BEFORE_DATE'; payload: string }
  | { type: 'SET_FILTER_ACCOUNT_TYPE'; payload: string }
  | { type: 'SET_FILTER_PERIOD'; payload: string }
  | { type: 'RESET_AFTER_TRANSITION' }
  | { type: 'RESET_SELECTION' };

const initialState: StatementsState = {
  pagination: {
    page: 1,
    pageSize: 15,
  },
  selection: {
    selected: [],
    transitionAll: false,
  },
  ui: {
    transitioning: false,
    openConfirmationDialog: false,
    currentStatement: null,
  },
  filters: {
    typeGroup: 'ANY',
    afterDate: '',
    beforeDate: '',
    accountType: 'ANY',
    period: 'ANY',
  },
};

const statementsReducer = (state: StatementsState, action: StatementsAction): StatementsState => {
  switch (action.type) {
    case 'SET_PAGE':
      return {
        ...state,
        pagination: { ...state.pagination, page: action.payload },
        selection: { ...state.selection, transitionAll: false },
      };

    case 'SET_PAGE_SIZE':
      return {
        ...state,
        pagination: { ...state.pagination, pageSize: action.payload },
      };

    case 'SET_SELECTED':
      return {
        ...state,
        selection: { ...state.selection, selected: action.payload },
      };

    case 'TOGGLE_SELECTION': {
      const isSelected = state.selection.selected.includes(action.payload);
      return {
        ...state,
        selection: {
          ...state.selection,
          selected: isSelected ? state.selection.selected.filter((id) => id !== action.payload) : [...state.selection.selected, action.payload],
          transitionAll: false,
        },
      };
    }

    case 'SELECT_ALL_ON_PAGE':
      return {
        ...state,
        selection: {
          ...state.selection,
          selected: Array.from(new Set([...state.selection.selected, ...action.payload])),
        },
      };

    case 'DESELECT_ALL_ON_PAGE':
      return {
        ...state,
        selection: {
          ...state.selection,
          selected: state.selection.selected.filter((id) => !action.payload.includes(id)),
          transitionAll: false,
        },
      };

    case 'SET_TRANSITION_ALL':
      return {
        ...state,
        selection: { ...state.selection, transitionAll: action.payload },
      };

    case 'SET_TRANSITIONING':
      return {
        ...state,
        ui: { ...state.ui, transitioning: action.payload },
      };

    case 'SET_CONFIRMATION_DIALOG':
      return {
        ...state,
        ui: { ...state.ui, openConfirmationDialog: action.payload },
      };

    case 'SET_FILTER_TYPE_GROUP':
      return {
        ...state,
        filters: { ...state.filters, typeGroup: action.payload },
      };

    case 'SET_FILTER_AFTER_DATE':
      return {
        ...state,
        filters: { ...state.filters, afterDate: action.payload },
      };

    case 'SET_FILTER_BEFORE_DATE':
      return {
        ...state,
        filters: { ...state.filters, beforeDate: action.payload },
      };

    case 'SET_FILTER_ACCOUNT_TYPE':
      return {
        ...state,
        filters: { ...state.filters, accountType: action.payload },
      };

    case 'SET_FILTER_PERIOD':
      return {
        ...state,
        filters: { ...state.filters, period: action.payload },
      };

    case 'RESET_AFTER_TRANSITION':
      return {
        ...state,
        pagination: { ...state.pagination, page: 1 },
        selection: { selected: [], transitionAll: false },
        ui: {
          ...state.ui,
          transitioning: false,
          openConfirmationDialog: false,
        },
      };

    case 'RESET_SELECTION':
      return {
        ...state,
        selection: { selected: [], transitionAll: false },
      };

    default:
      return state;
  }
};

export const StatementsReview = () => {
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['statementsReview', 'client']);

  const [state, dispatch] = useReducer(statementsReducer, initialState);

  const [transitionAccountStatements] = useMutation(TRANSITION_ACCOUNT_STATEMENTS);
  const [fetchAccountStatement] = useLazyQuery(FETCH_ACCOUNT_STATEMENT, { fetchPolicy: 'network-only' });

  const client = useApolloClient();

  const {
    data, loading, refetch,
  } = useQuery(FETCH_STATEMENTS_FOR_REVIEW, {
    skip: !activeOrganization.id,
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        filter: {
          state: StatementStates.AWAITING_REVIEW,
          organizationId: activeOrganization.id,
          afterDate: state.filters.afterDate === '' ? undefined : state.filters.afterDate,
          beforeDate: state.filters.beforeDate === '' ? undefined : state.filters.beforeDate,
          typeGroup: state.filters.typeGroup === 'ANY' ? undefined : state.filters.typeGroup,
          accountType: state.filters.accountType === 'ANY' ? undefined : state.filters.accountType,
        },
        pagination: {
          sortField: 'referenceDate',
          sortDesc: false,
          perPage: state.pagination.pageSize,
          offSet: (state.pagination.page - 1) * state.pagination.pageSize,
        },
      },
    },
  });

  const statementResponse = data?.fetchAccountStatements.accountStatements;
  const totalCount = data?.fetchAccountStatements.totalCount;

  const allItemsOnPageSelected = useMemo(
    () => statementResponse?.every((statement: Statement) => state.selection.selected.includes(statement.id)) && statementResponse?.length > 0,
    [statementResponse, state.selection.selected],
  );

  const allItemsSelected = useMemo(() => state.selection.selected.length === totalCount, [state.selection.selected.length, totalCount]);

  const indeterminate = useMemo(
    () => !loading && !allItemsOnPageSelected && state.selection.selected.length > 0 && statementResponse?.some((statement: Statement) => state.selection.selected.includes(statement.id)),
    [loading, allItemsOnPageSelected, state.selection.selected, statementResponse],
  );

  const [, setPageUrl] = usePageState(1, 'page');
  const [, setPageSizeUrl] = usePageState(15, 'pageSize');
  const [, setFilterTypeGroupUrl] = usePageState('ANY', 'typeGroup');
  const [, setFilterAfterDateUrl] = usePageState('', 'afterDate');
  const [, setFilterBeforeDateUrl] = usePageState('', 'beforeDate');
  const [, setFilterAccountTypeUrl] = usePageState('ANY', 'accountType');
  const [, setFilterPeriodUrl] = usePageState('ANY', 'period');

  useEffect(() => {
    setPageUrl(state.pagination.page);
  }, [state.pagination.page, setPageUrl]);

  useEffect(() => {
    setPageSizeUrl(state.pagination.pageSize);
  }, [state.pagination.pageSize, setPageSizeUrl]);

  useEffect(() => {
    setFilterTypeGroupUrl(state.filters.typeGroup);
  }, [state.filters.typeGroup, setFilterTypeGroupUrl]);

  useEffect(() => {
    setFilterAfterDateUrl(state.filters.afterDate);
  }, [state.filters.afterDate, setFilterAfterDateUrl]);

  useEffect(() => {
    setFilterBeforeDateUrl(state.filters.beforeDate);
  }, [state.filters.beforeDate, setFilterBeforeDateUrl]);

  useEffect(() => {
    setFilterAccountTypeUrl(state.filters.accountType);
  }, [state.filters.accountType, setFilterAccountTypeUrl]);

  useEffect(() => {
    setFilterPeriodUrl(state.filters.period);
  }, [state.filters.period, setFilterPeriodUrl]);

  useEffect(() => {
    dispatch({ type: 'SET_TRANSITION_ALL', payload: allItemsSelected });
  }, [allItemsSelected]);

  const onDocumentClick = (statement: Statement) => async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    fetchAccountStatement({
      variables: { accountStatementId: statement.id },
      onCompleted: (statementDoc) => {
        setTimeout(() => window.open(statementDoc.fetchAccountStatement.accountStatement.signedUrl, '_blank'));
      },
    });
  };

  const generateNewFromDate = (value: string): string => {
    if (value === 'YEAR_TO_DATE') {
      return dayjs().startOf('year').format('YYYY-MM-DD');
    }
    return '';
  };

  const handleTransitionStatements = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_TRANSITIONING', payload: true });

      await transitionAccountStatements({
        variables: {
          input: {
            transition: AccountStatementTransitions.RECONCILE,
            filter: {
              accountStatementIds: state.selection.selected,
            },
          },
        },
      });

      client.cache.evict({
        fieldName: 'fetchAccountStatements',
      });
      client.cache.gc();

      await refetch();
      dispatch({ type: 'RESET_AFTER_TRANSITION' });
    } catch (error) {
      dispatch({ type: 'SET_TRANSITIONING', payload: false });
    }
  };

  const handleTransitionAllStatements = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_TRANSITIONING', payload: true });

      await transitionAccountStatements({
        variables: {
          input: {
            transitionAll: true,
            transition: AccountStatementTransitions.RECONCILE,
            filter: {
              organizationId: activeOrganization.id,
              afterDate: state.filters.afterDate === '' ? undefined : state.filters.afterDate,
              beforeDate: state.filters.beforeDate === '' ? undefined : state.filters.beforeDate,
              typeGroup: state.filters.typeGroup === 'ANY' ? undefined : state.filters.typeGroup,
              accountType: state.filters.accountType === 'ANY' ? undefined : state.filters.accountType,
            },
          },
        },
      });

      client.cache.evict({
        fieldName: 'fetchAccountStatements',
      });
      client.cache.gc();

      await refetch();
      dispatch({ type: 'RESET_AFTER_TRANSITION' });
    } catch (error) {
      dispatch({ type: 'SET_TRANSITIONING', payload: false });
    }
  };

  const dialogConfirmation = (): Promise<void> => {
    if (state.selection.transitionAll) {
      return handleTransitionAllStatements();
    }
    return handleTransitionStatements();
  };

  return (
    <Card loading={loading}>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box display='flex' alignItems='center'>
          <StatementsSelectionInfo
            selected={state.selection.selected}
            allItemsOnPageSelected={allItemsOnPageSelected}
            transitionAll={state.selection.transitionAll}
            statementResponse={statementResponse}
            totalCount={totalCount}
            setTransitionAll={(transitionAll: boolean) => dispatch({ type: 'SET_TRANSITION_ALL', payload: transitionAll })}
            setSelected={(selected: string[]) => dispatch({ type: 'SET_SELECTED', payload: selected })}
          />
        </Box>
        <Box display='flex' alignItems='center' gap={2}>
          <FilterModal
            testIds={{
              dialogCloseButton: 'statements-review-filter-modal-close-button',
              dialogSubmitButton: 'statements-review-filter-modal-submit-button',
              dialogOpenButton: 'statements-review-filter-modal-open-button',
            }}
          >
            <Box display='flex' flexDirection='column' gap={2}>
              <SelectField
                testId='statements-review-filter-modal-period-select'
                label={t('client:statements.byPeriod')}
                fullWidth
                value={state.filters.period || ''}
                onChange={(e: any) => {
                  dispatch({ type: 'SET_FILTER_PERIOD', payload: e.target.value });
                  dispatch({ type: 'SET_FILTER_AFTER_DATE', payload: generateNewFromDate(e.target.value) });
                  dispatch({ type: 'SET_FILTER_BEFORE_DATE', payload: dayjs().format('YYYY-MM-DD') });
                }}
              >
                <MenuItem testId='statements-review-filter-modal-period-select-any' value='ANY'>{t('client:statements.allTime')}</MenuItem>
                <MenuItem testId='statements-review-filter-modal-period-select-year-to-date' value='YEAR_TO_DATE'>{t('client:statements.yearToDate')}</MenuItem>
                <MenuItem testId='statements-review-filter-modal-period-select-custom' value='CUSTOM'>{t('client:statements.custom')}</MenuItem>
              </SelectField>
              <DateField
                dataTestId='statements-review-filter-modal-from-date-field'
                label={t('client:statements.fromDate')}
                value={state.filters.afterDate || null}
                fullWidth
                onChange={(date: any) => {
                  dispatch({ type: 'SET_FILTER_PERIOD', payload: 'CUSTOM' });
                  dispatch({ type: 'SET_FILTER_AFTER_DATE', payload: dayjs(date?.toString()).format('YYYY-MM-DD') });
                }}
                renderInput={(params: any) => <TextField fullWidth {...params} />}
              />
              <DateField
                dataTestId='statements-review-filter-modal-to-date-field'
                label={t('client:statements.toDate')}
                fullWidth
                value={state.filters.beforeDate || null}
                onChange={(date: any) => {
                  dispatch({ type: 'SET_FILTER_BEFORE_DATE', payload: dayjs(date?.toString()).format('YYYY-MM-DD') });
                  dispatch({ type: 'SET_FILTER_PERIOD', payload: 'CUSTOM' });
                }}
                renderInput={(params: any) => <TextField fullWidth {...params} />}
              />
              <SelectField
                testId='statements-review-filter-modal-type-group-select'
                label={t('client:statements.typeGroup')}
                fullWidth
                value={state.filters.typeGroup || ''}
                onChange={(e: any) => dispatch({ type: 'SET_FILTER_TYPE_GROUP', payload: e.target.value })}
              >
                <MenuItem testId='statements-review-filter-modal-type-group-select-any' value='ANY'>{t('client:statements.any')}</MenuItem>
                <MenuItem testId='statements-review-filter-modal-type-group-select-tax' value='TAX'>{t('client:statements.tax')}</MenuItem>
                <MenuItem testId='statements-review-filter-modal-type-group-select-statements' value='STATEMENTS'>{t('client:statements.statements')}</MenuItem>
                <MenuItem testId='statements-review-filter-modal-type-group-select-management-fees' value='MANAGEMENT_FEES'>{t('client:statements.managementFees')}</MenuItem>
              </SelectField>
              <AccountTypeSelect
                testId='statements-review-filter-modal-account-type-select'
                size='medium' onChange={(e: any) => dispatch({ type: 'SET_FILTER_ACCOUNT_TYPE', payload: e })}
                value={state.filters.accountType || ''} />
            </Box>
          </FilterModal>
          <Button
            dataTestId='statements-review-transition-statements-button'
            disabled={state.selection.selected.length === 0 || !permissions.includes('transition:account_statement')}
            label={t('statementsReview:transitionStatements.transitionStatementsButton')}
            size='sm'
            onClick={() => dispatch({ type: 'SET_CONFIRMATION_DIALOG', payload: true })}
          />
        </Box>
      </CardContent>

      <Table aria-label='table'>
        <TableBody>
          <TableRow>
            <TableHeadCell>
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Checkbox
                  testId='statements-review-checkbox-all'
                  disabled={loading}
                  indeterminate={indeterminate}
                  checked={!loading && allItemsOnPageSelected}
                  onChange={(checked: boolean) => {
                    if (!loading && statementResponse) {
                      if (checked) {
                        dispatch({ type: 'SELECT_ALL_ON_PAGE', payload: statementResponse.map((x: any) => x.id) });
                      } else {
                        dispatch({ type: 'DESELECT_ALL_ON_PAGE', payload: statementResponse.map((x: any) => x.id) });
                      }
                    }
                  }}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </Box>
            </TableHeadCell>
            <TableHeadCell>{t('statementsReview:table.client')}</TableHeadCell>
            <TableHeadCell>{t('statementsReview:table.organization')}</TableHeadCell>
            <TableHeadCell>{t('statementsReview:table.account')}</TableHeadCell>
            <TableHeadCell>{t('statementsReview:table.statementState')}</TableHeadCell>
            <TableHeadCell>{t('statementsReview:table.statementType')}</TableHeadCell>
          </TableRow>

          {loading
            && [...Array(15)].map((x: any, i: number) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
                <TableCell>
                  <Skeleton width='100%' />
                </TableCell>
              </TableRow>
            ))}

          {!loading && data?.fetchAccountStatements?.accountStatements?.map((statement: any) => (
            <TableRow
              testId={`statements-review-table-row-${statement.id}`}
              hover
              key={statement.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 }, textDecoration: 'none', cursor: 'pointer' }}
              onClick={onDocumentClick(statement)}
            >
              <TableCell sx={controlColumnStyles}>
                <div style={controlWrapperStyles}>
                  <Checkbox
                    testId={`statements-review-checkbox-${statement.id}`}
                    checked={state.selection.selected.includes(statement.id)}
                    onClick={(e: any) => e.stopPropagation()}
                    onChange={(checked: boolean) => {
                      dispatch({ type: 'TOGGLE_SELECTION', payload: statement.id });
                    }}
                    customStyle={{
                      margin: 0,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                </div>
              </TableCell>
              <TableCell component='th' scope='row'>
                <MuiLink data-testid='statements-review-client-link' component={Link} to={`/clients/${statement.user.id}`}>
                  {generateClientNameString(statement.user, false, true)}
                </MuiLink>
              </TableCell>
              <TableCell component='th' scope='row'>
                {statement.user.organization.name}
              </TableCell>
              <TableCell component='th' scope='row'>
                <Badge label={t(`accountTypes:${statement.account.type}`)} />
              </TableCell>
              <TableCell component='th' scope='row'>
                {t(`statementsReview:statementStates.${statement.state}`)}
              </TableCell>
              <TableCell component='th' scope='row'>
                <Badge label={t(`statementsReview:statementTypes.${statement.type}`)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Pagination
        count={Math.ceil((totalCount ?? 0) / state.pagination.pageSize)}
        page={state.pagination.page}
        perPage={state.pagination.pageSize}
        onChangePerPage={(newPageSize) => dispatch({ type: 'SET_PAGE_SIZE', payload: newPageSize })}
        onChange={(_e, newPage) => {
          dispatch({ type: 'SET_PAGE', payload: newPage });
        }}
        sx={{
          p: 1,
          textAlign: 'right',
          '.MuiPagination-ul': {
            justifyContent: 'end',
          },
        }}
      />

      <ConfirmationDialog
        testIdPrefix='statements-review-confirmation-dialog'
        open={state.ui.openConfirmationDialog}
        loading={state.ui.transitioning}
        onConfirm={dialogConfirmation}
        onCancel={() => dispatch({ type: 'SET_CONFIRMATION_DIALOG', payload: false })}
        title={t('statementsReview:transitionStatements.title')}
        bodyText={t('statementsReview:transitionStatements.bodyText', { totalCount: state.selection.transitionAll ? totalCount : state.selection.selected.length })}
        confirmButtonColor={ConfirmButtonColors.primary}
      ></ConfirmationDialog>
    </Card>
  );
};
